import {
	type FormMetadata,
	getSelectProps,
	useInputControl,
} from '@conform-to/react'
import { format } from 'date-fns'
import React, { useId } from 'react'
import { useLocation } from 'react-router-dom'
import { usoDeCFDIMap } from '#app/utils/usoDeCFDI.ts'
import { vendors } from '#app/utils/vendors.ts'
import { type VendorsType } from '#types/vendors.js'
import { Checkbox, type CheckboxProps } from './ui/checkbox.tsx'
import { Input } from './ui/input.tsx'
import { Label } from './ui/label.tsx'
import { Textarea } from './ui/textarea.tsx'

export type ListOfErrors = Array<string | null | undefined> | null | undefined
type FieldType = ReturnType<FormMetadata<any, any>['getFieldset']>[string]

export function ErrorList({
	id,
	errors,
}: {
	errors?: ListOfErrors
	id?: string
}) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<ul id={id} className="flex flex-col gap-1">
			{errorsToRender.map(e => (
				<li key={e} className="text-[10px] text-foreground-destructive">
					{e}
				</li>
			))}
		</ul>
	)
}

export function Field({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	const hasDateValue =
		inputProps.type === 'date' && inputProps.defaultValue
			? format(new Date(inputProps.defaultValue as string), 'yyyy-MM-dd')
			: undefined
	const modifiedDefaultValue =
		inputProps.type === 'date' ? hasDateValue : inputProps.defaultValue
	const modifiedInputProps = {
		...inputProps,
		defaultValue: modifiedDefaultValue,
	}
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Input
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...modifiedInputProps}
			/>
			<div className="min-h-[32px] px-4 pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function TextareaField({
	labelProps,
	textareaProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Textarea
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...textareaProps}
			/>
			<div className="min-h-[32px] px-4 pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function CheckboxField({
	labelProps,
	buttonProps,
	errors,
	className,
}: {
	labelProps: JSX.IntrinsicElements['label']
	buttonProps: CheckboxProps & {
		name: string
		form: string
		value?: string
	}
	errors?: ListOfErrors
	className?: string
}) {
	const { key, defaultChecked, ...checkboxProps } = buttonProps
	const fallbackId = useId()
	const checkedValue = buttonProps.value ?? 'on'
	const input = useInputControl({
		key,
		name: buttonProps.name,
		formId: buttonProps.form,
		initialValue: defaultChecked ? checkedValue : undefined,
	})
	const id = buttonProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<div className="flex gap-2">
				<Checkbox
					{...checkboxProps}
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					checked={input.value === checkedValue}
					onCheckedChange={state => {
						input.change(state.valueOf() ? checkedValue : '')
						buttonProps.onCheckedChange?.(state)
					}}
					onFocus={event => {
						input.focus()
						buttonProps.onFocus?.(event)
					}}
					onBlur={event => {
						input.blur()
						buttonProps.onBlur?.(event)
					}}
					type="button"
				/>
				<label
					htmlFor={id}
					{...labelProps}
					className="self-center text-body-xs text-muted-foreground"
				/>
			</div>
			<div className="px-4 pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function SelectVendorField({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLSelectElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<div className="flex flex-col">
				<Label htmlFor={id} {...labelProps} />
				<select
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid]:border-input-invalid"
					{...inputProps}
				>
					<option value="" hidden />
					{Object.keys(vendors).map(key => {
						return (
							<option key={key} value={key}>
								{vendors[key as VendorsType].label}
							</option>
						)
					})}
				</select>
			</div>
			<div className="min-h-[32px] px-4 pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function SelectField({
	labelProps,
	inputProps,
	errors,
	className,
	mapOfOptions,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.SelectHTMLAttributes<HTMLSelectElement>
	errors?: ListOfErrors
	className?: string
	mapOfOptions: Record<string, any>
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<div className="flex flex-col">
				<Label htmlFor={id} {...labelProps} />
				<select
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid]:border-input-invalid"
					{...inputProps}
				>
					<option value="" hidden />
					{Object.keys(mapOfOptions).map(key => {
						return (
							<option key={key} value={key}>
								{mapOfOptions[key].label}
							</option>
						)
					})}
				</select>
			</div>
			<div className="min-h-[32px] px-4 pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}
export function UsoDeCFDIField({ field }: { field: FieldType }) {
	return (
		<SelectField
			labelProps={{ children: 'Uso de CFDI' }}
			inputProps={{
				...getSelectProps(field),
			}}
			errors={field.errors}
			mapOfOptions={usoDeCFDIMap}
		/>
	)
}
export function FormaDePagoField({ field }: { field: FieldType }) {
	const page = useLocation().pathname
	const isSimilares = page.includes('fasimilares')
	const hideMonederoElectronico = isSimilares
	const hideTarjetaDeDebito = isSimilares
	const mapOfOptions = {
		'01': { label: 'Efectivo' },
		'02': { label: 'Cheque nominativo' },
		'03': { label: 'Transferencia electrónica de fondos' },
		'04': { label: 'Tarjeta de crédito' },
		...(!hideMonederoElectronico && {
			'05': { label: 'Monedero electrónico' },
		}),
		...(!hideTarjetaDeDebito && { '06': { label: 'Vales de despensa' } }),
		'07': { label: 'Tarjeta de débito' },
	}

	return (
		<SelectField
			labelProps={{ children: 'Forma de Pago' }}
			inputProps={{
				...getSelectProps(field),
			}}
			errors={field.errors}
			mapOfOptions={mapOfOptions}
		/>
	)
}
